/**
 * Page metadata configuration for SEO and browser titles
 */
export const PAGE_METADATA = {
  // Auth pages
  login: {
    title: "Sign In",
    description:
      "Sign in to Casekeep to access your secure file sharing dashboard. Manage encrypted client documents, e-signatures, and case workflows with bank-level security.",
  },

  // Organization pages
  organizationDashboard: {
    title: "Organization Dashboard",
    description:
      "Manage your organization settings, view team members, and access secure case management tools. Track activity with detailed audit logs and role-based permissions.",
  },
  memberDetail: {
    title: "Team Member Details",
    description:
      "View and manage team member information, permissions, and case assignments. Control access levels with granular role-based security settings.",
  },

  // Case pages
  cases: {
    title: "Cases",
    description:
      "Manage and track all your cases in one secure location with end-to-end encrypted file storage. Organize client documents, forms, and e-signatures by case.",
  },
  caseDetail: {
    title: "Case Details",
    description:
      "View case information, encrypted files, form submissions, e-signatures, and complete activity history. Track every action with detailed audit logging.",
  },
  createCase: {
    title: "Create New Case",
    description:
      "Create a new case to organize client documents, collect information via secure forms, and manage e-signatures. All data encrypted with bank-level security.",
  },
  assignUser: {
    title: "Assign User to Case",
    description:
      "Assign team members or clients to cases for secure collaboration. Control access levels with granular folder permissions and role-based security.",
  },

  // Form pages
  forms: {
    title: "Form Templates",
    description:
      "Create and manage reusable form templates for secure client intake and data collection. Send forms via magic link with no client login required.",
  },
  createForm: {
    title: "Create Form Template",
    description:
      "Design a new form template for secure case data collection. Build custom fields and file upload requests to gather encrypted client information.",
  },
  editForm: {
    title: "Edit Form Template",
    description:
      "Modify your form template fields, file upload settings, and configurations. Changes apply to future form requests while preserving existing submissions.",
  },
  formSubmissions: {
    title: "Form Submissions",
    description:
      "View and manage all encrypted form submissions across your cases. Access submitted data and attached files with complete audit trail visibility.",
  },
  formSubmissionView: {
    title: "Form Submission",
    description:
      "View encrypted form submission details and securely download attached files. Track access history with comprehensive audit logging.",
  },
  createCaseForm: {
    title: "Create Form Request",
    description:
      "Add a new form request to this case for secure data collection. Generate a magic link to send clients with no account signup required.",
  },
  editCaseForm: {
    title: "Edit Form Request",
    description:
      "Update the form request configuration, notification settings, and reminder schedules. Manage secure link expiration and access controls.",
  },

  // File pages
  files: {
    title: "Files",
    description:
      "Securely manage, upload, and share files with AES-256 end-to-end encryption. Create expiring share links with download limits and email verification.",
  },
  uploadFile: {
    title: "Upload Files",
    description:
      "Upload and encrypt files for secure storage and sharing. Files are encrypted with unique keys before upload using bank-level AES-256-GCM encryption.",
  },
  fileDetail: {
    title: "File Details",
    description:
      "View encrypted file details, securely download, and manage sharing permissions. Create expiring links with optional email verification for recipients.",
  },

  // Client portal pages
  clientDashboard: {
    title: "Client Dashboard",
    description:
      "Access your secure client portal to view case information, download documents, and complete pending form requests. All data protected with bank-level encryption.",
  },
  clientDocuments: {
    title: "My Documents",
    description:
      "Access all your case documents and files in one secure place. Download encrypted files shared by your professional with complete privacy protection.",
  },

  // Public pages
  sharedResource: {
    title: "Shared Resource",
    description:
      "Access a securely shared file or form submission. This link provides encrypted access to protected content with full audit trail logging.",
  },
  publicCaseForm: {
    title: "Submit Form",
    description:
      "Securely submit your information and documents. Your data is encrypted with bank-level security and no account registration is required.",
  },
  verifyMagicLink: {
    title: "Verify Access",
    description:
      "Verifying your secure magic link access. Email verification ensures only authorized recipients can access protected documents and files.",
  },

  // Other pages
  settings: {
    title: "Settings",
    description:
      "Manage your user settings, notification preferences, and security options. Configure two-factor authentication and account security settings.",
  },
  auditLog: {
    title: "Audit Log",
    description:
      "View detailed activity logs and track all actions in your organization. Export compliance reports with complete access history and IP tracking.",
  },
  superadmin: {
    title: "Superadmin Dashboard",
    description:
      "System administration and organization management dashboard. Monitor platform health, manage organizations, and view system-wide analytics.",
  },
  notFound: {
    title: "Page Not Found",
    description:
      "The page you're looking for doesn't exist or has been moved. Return to your dashboard to continue managing secure client documents.",
  },
} as const;
